/**
 * 投稿・更新日時の表示用（X 相当の相対・省略表記）。
 * - 当日・1時間未満: 35分
 * - 当日・1時間以上: 3時間
 * - 当日以外・同年: 3月22日
 * - 年が異なる: 2025年3月22日
 */

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function clockHM(d: Date): string {
  return `${d.getHours()}:${pad2(d.getMinutes())}`;
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function sameYear(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear();
}

/**
 * @param dateInput ISO 文字列または Date
 * @param nowInput テスト用の基準時刻（省略時は現在）
 */
export function formatPostTime(dateInput: string | Date, nowInput: string | Date = new Date()): string {
  const d = dateInput instanceof Date ? new Date(dateInput.getTime()) : new Date(dateInput);
  const now = nowInput instanceof Date ? new Date(nowInput.getTime()) : new Date(nowInput);
  if (Number.isNaN(d.getTime())) {
    return '';
  }

  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  // 未来の日時（時計ずれ等）は絶対表記にフォールバック
  if (diffMs < 0) {
    if (sameCalendarDay(d, now)) return clockHM(d);
    if (sameYear(d, now)) return `${d.getMonth() + 1}月${d.getDate()}日`;
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  }

  if (sameCalendarDay(d, now)) {
    if (diffMin < 60) {
      const m = Math.max(1, diffMin);
      return `${m}分`;
    }
    const hours = Math.floor(diffMs / 3_600_000);
    return `${Math.max(1, hours)}時間`;
  }

  if (sameYear(d, now)) {
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  }
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}

/**
 * 作成日時より更新日時が後ろにある場合に true（編集済みラベル用）。
 * DB の同一挿入時刻のわずかな差は 1 秒未満なら未編集とみなす。
 */
export function isEditedSinceCreated(createdAt: string, updatedAt: string): boolean {
  const c = new Date(createdAt).getTime();
  const u = new Date(updatedAt).getTime();
  if (!Number.isFinite(c) || !Number.isFinite(u)) return false;
  return u - c > 1000;
}

/**
 * 掲示板の表示期限の表示用。
 * - 当日（カレンダー日が「今」と同じ）: 時刻のみ（例: 14:30）
 * - それ以外・同年: 日付のみ（例: 4月11日）
 * - 年が異なる: 年付き（例: 2027年4月11日）
 */
export function formatDisplayExpiresAt(
  dateInput: string | Date,
  nowInput: string | Date = new Date()
): string {
  const d = dateInput instanceof Date ? new Date(dateInput.getTime()) : new Date(dateInput);
  const now = nowInput instanceof Date ? new Date(nowInput.getTime()) : new Date(nowInput);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  if (sameCalendarDay(d, now)) {
    return clockHM(d);
  }
  if (sameYear(d, now)) {
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  }
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
}
